/**
 * StreaksDisplay — shows processing streak and weekly streak with visual flair.
 *
 * Processing Streak: consecutive files completed within SLA. Resets on breach.
 * Weekly Streak: consecutive weeks with X+ qualifying files.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Flame, Zap, Trophy } from 'lucide-react';

export default function StreaksDisplay({ processingStreak = 0, weeklyStreak = 0 }) {
  return (
    <div style={styles.container}>
      <StreakBadge
        icon={Flame}
        count={processingStreak}
        label="SLA Streak"
        color="#F4A261"
        description="Consecutive files within SLA"
      />
      <StreakBadge
        icon={Zap}
        count={weeklyStreak}
        label="Week Streak"
        color="#1B9AAA"
        description="Consecutive qualifying weeks"
      />
    </div>
  );
}

function StreakBadge({ icon: Icon, count, label, color, description }) {
  const isActive = count > 0;

  return (
    <motion.div
      style={{
        ...styles.badge,
        background: isActive
          ? `linear-gradient(135deg, ${color}10, ${color}18)`
          : '#F0F4F8',
        border: isActive
          ? `1px solid ${color}30`
          : '1px solid #E8EFF5',
      }}
      whileHover={{ scale: 1.02 }}
      title={description}
    >
      <div style={{ ...styles.iconWrap, background: isActive ? `${color}20` : '#E8EFF5' }}>
        <Icon size={18} color={isActive ? color : '#6B7A8D'} />
      </div>
      <div>
        <motion.div
          style={{ ...styles.count, color: isActive ? color : '#6B7A8D' }}
          key={count}
          initial={{ scale: 1.4 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          {count}
        </motion.div>
        <div style={styles.label}>{label}</div>
      </div>
      {count >= 10 && (
        <Trophy size={14} color={color} style={{ marginLeft: 'auto', opacity: 0.6 }} />
      )}
    </motion.div>
  );
}

const styles = {
  container: {
    display: 'flex',
    gap: '0.6rem',
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    borderRadius: '10px',
    flex: 1,
    minWidth: 0,
  },
  iconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    flexShrink: 0,
  },
  count: {
    fontSize: '1.15rem',
    fontWeight: 700,
    lineHeight: 1.1,
  },
  label: {
    fontSize: '0.65rem',
    color: '#6B7A8D',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
};
